import { CheckCircle2, ExternalLink, Loader2, Sparkles, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { formatAddressLines } from "@shared/address";
import type { CompanyData } from "../companyTypes";
import type { ProspectEnrichment } from "./useProspectEnrichment";

/** The two-column (Apollo and AI) review modal. */
export function ProspectEnrichReview({ enrich, company }: { enrich: ProspectEnrichment; company: CompanyData }) {
  const {
    phase: enrichPhase, result: enrichResult, selected: selectedFields, setSelected: setSelectedFields,
    setPhase: setEnrichPhase, applying: enrichApplying, apply: applyProspectEnrich,
  } = enrich;
  if (!(enrichPhase === "review" && enrichResult)) return null;
  return (
    <>
            <div role="dialog" aria-modal="true" aria-label="Enrichment results" data-state="open" className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center pt-8 pb-8 overflow-y-auto" onClick={() => setEnrichPhase("idle")}>
              <div className="bg-background rounded-xl max-w-3xl w-full mx-4 flex flex-col" style={{ maxHeight: "calc(100vh - 4rem)" }} onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className="px-6 pt-5 pb-3 border-b shrink-0">
                  <h2 className="text-base font-bold flex items-center gap-2"><Zap className="w-4 h-4 text-amber-500" /> Enrichment Results</h2>
                  <p className="text-xs text-muted-foreground mt-1">Select data to apply to {company.name}</p>
                  {enrichResult.aiError && <p className="text-xs text-amber-600 mt-0.5 dark:text-amber-400">AI: {enrichResult.aiError}</p>}
                </div>

                {/* Scrollable content */}
                <div className="overflow-y-auto flex-1 px-6 py-3" style={{ minHeight: 0 }}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  {/* ─── LEFT: Apollo Data ─── */}
                  <div>
                    <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3 flex items-center gap-1.5">
                      <ExternalLink className="w-3.5 h-3.5" /> Apollo Data
                    </h3>
                    <div className="space-y-2">
                      {(() => {
                        const a = enrichResult.apollo || {};
                        const items: { key: string; label: string; value: any; warn?: string }[] = [];
                        if (a.industry) items.push({ key: "apolloIndustry", label: "Industry", value: a.industry, warn: company.industry ? `Current: ${company.industry}` : undefined });
                        if (a.headquarters) items.push({ key: "apolloHq", label: "Location", value: a.headquarters, warn: company.headquarters ? `Current: ${company.headquarters}` : undefined });
                        if (a.employeeCount) items.push({ key: "apolloEmployees", label: "Employees", value: a.employeeCount });
                        if (a.revenueRange) items.push({ key: "apolloRevenue", label: "Est. Revenue", value: a.revenueRange, warn: company.revenueRange ? `Current: ${company.revenueRange}` : undefined });
                        if (a.linkedinUrl) items.push({ key: "apolloLinkedin", label: "LinkedIn", value: a.linkedinUrl });
                        if (a.companyAddress) items.push({ key: "apolloAddress", label: "Company address", value: formatAddressLines(a.companyAddress).join(", "), warn: enrichResult.current?.hasCompanyAddress ? "Replaces the saved company address" : undefined });
                        if (a.phone) items.push({ key: "apolloPhone", label: "Phone", value: a.phone });
                        if (a.foundedYear) items.push({ key: "apolloFounded", label: "Founded", value: a.foundedYear });
                        if (a.apolloId) items.push({ key: "apolloId", label: "Apollo Profile", value: "Link to Apollo" });
                        if (items.length === 0) return <p className="text-xs text-muted-foreground italic py-4">No Apollo data available</p>;
                        return items.map(item => (
                          <label key={item.key} className="flex items-start gap-2.5 p-2.5 rounded-lg border hover:bg-muted/50 cursor-pointer">
                            <Checkbox checked={!!selectedFields[item.key]} onCheckedChange={(v) => setSelectedFields(p => ({ ...p, [item.key]: !!v }))} className="mt-0.5" />
                            <div className="flex-1 min-w-0">
                              <div className="text-xs font-medium text-muted-foreground">{item.label}</div>
                              <p className="text-sm truncate">{item.value}</p>
                              {item.warn && <p className="text-xs text-amber-600 dark:text-amber-400">{item.warn}</p>}
                            </div>
                          </label>
                        ));
                      })()}
                    </div>
                  </div>

                  {/* ─── RIGHT: AI Research ─── */}
                  <div>
                    <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3 flex items-center gap-1.5">
                      <Sparkles className="w-3.5 h-3.5" /> AI Research
                    </h3>
                    <div className="space-y-2">
                      {(() => {
                        const ai = enrichResult.ai;
                        if (!ai) return <p className="text-xs text-muted-foreground italic py-4">{enrichResult.aiError || "AI research not available"}</p>;
                        return (
                          <>
                            {ai.companyDescription && (
                              <label className="flex items-start gap-2.5 p-2.5 rounded-lg border hover:bg-muted/50 cursor-pointer">
                                <Checkbox checked={!!selectedFields.aiDescription} onCheckedChange={(v) => setSelectedFields(p => ({ ...p, aiDescription: !!v }))} className="mt-0.5" />
                                <div className="flex-1 min-w-0">
                                  <div className="text-xs font-medium text-muted-foreground">Company Description</div>
                                  <p className="text-sm line-clamp-4">{ai.companyDescription}</p>
                                  {company.description && <p className="text-xs text-amber-600 dark:text-amber-400">Will overwrite existing</p>}
                                </div>
                              </label>
                            )}
                            {ai.products?.length > 0 && (
                              <div className="p-2.5 rounded-lg border bg-muted/20">
                                <div className="text-xs font-medium text-muted-foreground mb-1">Products / Services</div>
                                <p className="text-sm">{ai.products.join(", ")}</p>
                              </div>
                            )}
                            {ai.ownerName && (
                              <label className="flex items-start gap-2.5 p-2.5 rounded-lg border hover:bg-muted/50 cursor-pointer">
                                <Checkbox checked={!!selectedFields.aiOwner} onCheckedChange={(v) => setSelectedFields(p => ({ ...p, aiOwner: !!v }))} className="mt-0.5" />
                                <div>
                                  <div className="text-xs font-medium text-muted-foreground">Owner / Key Person</div>
                                  <p className="text-sm">{ai.ownerName}{ai.ownerTitle ? `, ${ai.ownerTitle}` : ""}</p>
                                </div>
                              </label>
                            )}
                            {ai.whyGoodProspect && (
                              <label className="flex items-start gap-2.5 p-2.5 rounded-lg border border-amber-200 bg-amber-50/50 hover:bg-amber-50 cursor-pointer dark:border-amber-900 dark:bg-amber-950/40">
                                <Checkbox checked={!!selectedFields.aiThesis} onCheckedChange={(v) => setSelectedFields(p => ({ ...p, aiThesis: !!v }))} className="mt-0.5" />
                                <div className="flex-1 min-w-0">
                                  <div className="text-xs font-medium text-amber-700 dark:text-amber-400">Prospect Thesis</div>
                                  <p className="text-sm">{ai.whyGoodProspect}</p>
                                </div>
                              </label>
                            )}
                            {ai.estimatedDealValue && (
                              <label className="flex items-start gap-2.5 p-2.5 rounded-lg border border-green-200 bg-green-50/50 hover:bg-green-50 cursor-pointer dark:border-green-900 dark:bg-green-950/40">
                                <Checkbox checked={!!selectedFields.aiDealValue} onCheckedChange={(v) => setSelectedFields(p => ({ ...p, aiDealValue: !!v }))} className="mt-0.5" />
                                <div className="flex-1 min-w-0">
                                  <div className="text-xs font-medium text-green-700 dark:text-green-400">Est. Deal Value</div>
                                  <p className="text-sm font-semibold">{ai.estimatedDealValue.estimate}</p>
                                  <p className="text-xs text-muted-foreground mt-0.5">{ai.estimatedDealValue.reasoning}</p>
                                  <p className="text-xs text-muted-foreground">Multiple: {ai.estimatedDealValue.multipleUsed} · Confidence: {ai.estimatedDealValue.confidence}</p>
                                </div>
                              </label>
                            )}
                            {ai.estimatedRevenue && !enrichResult.apollo?.revenueRange && (
                              <label className="flex items-start gap-2.5 p-2.5 rounded-lg border hover:bg-muted/50 cursor-pointer">
                                <Checkbox checked={!!selectedFields.aiRevenue} onCheckedChange={(v) => setSelectedFields(p => ({ ...p, aiRevenue: !!v }))} className="mt-0.5" />
                                <div>
                                  <div className="text-xs font-medium text-muted-foreground">Est. Revenue (AI)</div>
                                  <p className="text-sm">{ai.estimatedRevenue}</p>
                                </div>
                              </label>
                            )}
                            {ai.yearFounded && !enrichResult.apollo?.foundedYear && (
                              <div className="p-2.5 rounded-lg border bg-muted/20">
                                <div className="text-xs font-medium text-muted-foreground">Founded (from website)</div>
                                <p className="text-sm">{ai.yearFounded}</p>
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  </div>
                </div>

                </div>
                {/* Footer */}
                <div className="flex gap-2 justify-end px-6 py-3 border-t shrink-0">
                  <Button variant="outline" onClick={() => setEnrichPhase("idle")}>Cancel</Button>
                  <Button onClick={applyProspectEnrich} disabled={enrichApplying || !Object.values(selectedFields).some(Boolean)} className="gap-1.5">
                    {enrichApplying ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Applying...</> : <><CheckCircle2 className="w-3.5 h-3.5" /> Apply Selected</>}
                  </Button>
                </div>
              </div>
            </div>
    </>
  );
}
